import { CommonModule } from "@angular/common";
import { Component, Input, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Observable } from "rxjs";
import { AlertService } from "@full-fledged/alerts";
import { FormAccessEntitlements } from "app/shared/services/model/form-service.model";
import { FormNavigationService } from "../form-navigation.service";
import { FormService } from "app/modules/forms/form.service";
import { GlobalService } from "app/shared/services/global.service";
import { ModalAuthService } from "app/modules/auth/modal-auth/modal-auth.service";
import { SaveApplicationResponse, SubmitApplicationResponse } from "app/shared/services/model/application-service.model";
import { UserService } from "app/core/user/user.service";

import { AccessLevel, FormPage, ProductDefinition, UserFormPage } from "app/shared/model";
import { Router } from "@angular/router";
import { FormNavigationItem } from "../navigation.model";
import { SaveAndPrintButtonComponent } from "app/modules/custom/save-and-print-button/save-and-print-button.component";
import { SaveAndReturnButtonComponent } from "app/modules/custom/save-and-return-button/save-and-return-button.component";
import { hasValue } from "app/shared";

@Component({
    selector    : 'iapply-navigation-menu',
    templateUrl : './nav-menu.component.html',
    styleUrl    : './nav-menu.component.scss',
    imports     : [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        SaveAndPrintButtonComponent,
        SaveAndReturnButtonComponent,
    ]
})
export class NavigationMenuComponent {
    @Input() page: FormPage | UserFormPage;

    private readonly _globalService = inject(GlobalService);
    protected readonly _navigationService = inject(FormNavigationService);
    private readonly _userService = inject(UserService);
    private readonly _alertService = inject(AlertService);
    private readonly _formService = inject(FormService);
    private readonly _modalAuthService = inject(ModalAuthService);
    private readonly _router = inject(Router);
    private _hasUser: boolean = false;

    menuItems$: Observable<FormNavigationItem[]>;
    currentMenuItem$: Observable<FormNavigationItem>;
    nextMenuItem$: Observable<FormNavigationItem>;
    previousMenuItem$: Observable<FormNavigationItem>;
    busy$: Observable<boolean>;

    isSubmitted = signal(false);

    get isBusy(): boolean {
        return this._navigationService.isBusy?.() ?? false;
    }

    productAccessLevel: AccessLevel = AccessLevel.NoAccess;
    applicationEntitlement: FormAccessEntitlements;
    form: ProductDefinition;
    preview: boolean = false;
    errors: any = {};
    canAutoSave: boolean = false;
    submitText: string = 'Submit'
    // submitResponse: SubmitApplicationResponse;

    constructor() {
        this.menuItems$ = this._navigationService.menuItems$;
        this.currentMenuItem$ = this._navigationService.currentMenuItem$;
        this.nextMenuItem$ = this._navigationService.nextMenuItem$;
        this.previousMenuItem$ = this._navigationService.previousMenuItem$;

        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntilDestroyed())
            .subscribe((user) => {
                this._hasUser = !!user;
            });

        this._formService.entitlements$
            .pipe(takeUntilDestroyed())
            .subscribe((entitlement) => {
                this.applicationEntitlement = entitlement;
                this.canAutoSave = !!entitlement?.autoSaveEnabled;
            });

        this._formService.product$
            .pipe(takeUntilDestroyed())
            .subscribe((form: ProductDefinition) => {
                this.form = form;
            });

        this.currentMenuItem$
            .pipe(takeUntilDestroyed())
            .subscribe((_: FormNavigationItem) => {
                const pages = this.form?.pages || [];
                const pageId = this._formService.currentState.pageId;
                if (pageId) {
                    this.isSubmitted.set(pageId == 999);
                    const currentPage = pages.find(p => p.pageId == pageId);
                    this.submitText = (<UserFormPage>currentPage)?.submitLabel || 'Submit';
                }
            });
    }

    saveApplication() {
        if (this.isBusy) {
            return;
        }

        if (!this._hasUser) {
            this.handleAuthModal();
            return;
        }

        this.doSaveApplication()
    }

    handleAuthModal() {
        this._modalAuthService.showAuthRequiredModal(false, this.doSaveApplication.bind(this))
    }

    doSaveApplication(didCreateUser?: boolean, publicDidAuthenticate?: boolean) {
        if (this.isBusy) {
            return;
        }
        this._navigationService.saveApplication(this.canAutoSave, didCreateUser, publicDidAuthenticate)
            .subscribe({
                next: (result: SaveApplicationResponse) => {
                    // TODO: Alert messages in language pack
                    if (result.success) {
                        this._alertService.success('Application saved successfully.');
                    } else {
                        this._alertService.danger('Application could not be saved.');
                    }
                },
                error: (err) => {
                    this._alertService.danger('There was an error saving your application.');
                },
                complete: console.info
            });
    }

    submitApplication() {
        if (!this._hasUser && this._globalService.isOffline()) {
            this._alertService.danger("You can't submit when offline if you're not logged in.");
            return;
        }

        if (this.isBusy) {
            return;
        }

        this._navigationService.submitApplication()
            .subscribe({
                next: (result: SubmitApplicationResponse) => {
                    // TODO: Alert messages in language pack
                    if (result.success && result.valid) {
                        // this._alertService.success('Application submitted successfully.');
                    } else {
                        console.log('Nav Menu - Submit Errors:', result.errors);
                        this._alertService.danger(this.hasValidationFailure(result)
                            ? 'Please correct the validation errors present on this page'
                            : 'Application could not be updated.');
                    }
                },
                error: this._alertService.danger,
                complete: console.info
            });
    }

    private hasValidationFailure(result: SubmitApplicationResponse): boolean {
        return result.valid === false && (hasValue(result.errors) || result.firstPage?.pageId != null);
    }

}
